import React from 'react';
import {
  IPlanOverviewDetail,
  ISubjectSubscription,
  SubscriptionStatus,
} from '@models/interfaces/user.interface';
import TriangleExclamation from '@assets/icons/triangle-exclamation';
import moment from 'moment/moment';
import { isInAppPurchase } from '@components/subscription/overview/helper/PlanDetailHelper';
import {
  getRenewalDate,
  getRenewalPrice,
} from '@components/subscription/helper/SubscriptionHelper';

export interface WarningNoteProps {
  planDetail: IPlanOverviewDetail;
  subscription: ISubjectSubscription;
  className?: string;
}

const WarningNote = ({
  planDetail,
  subscription,
  className,
}: WarningNoteProps) => {
  const expiryDate = moment(subscription?.expiry_date + '+08:00').format(
    'D MMMM YYYY',
  );
  const renewDate = getRenewalDate(subscription, planDetail).format(
    'D MMMM YYYY',
  );
  const WarningContent = () => {
    const period = planDetail.billing_period.toLowerCase();
    const timePeriod =
      period === 'year' || planDetail.billing_frequency == 1
        ? period
        : planDetail.billing_frequency + ' ' + period + 's';

    if (subscription.is_trial_plan) {
      return <>Subscription automatically renews every {timePeriod}</>;
    }

    if (!subscription.auto_renewal) {
      return (
        <>
          Your subscription will end on{' '}
          <span className={'font-bold'}>{expiryDate}</span> and you will lose
          access to all of Geniebook's features thereafter.
          {planDetail.subscription_status !=
            SubscriptionStatus.activeAutoRenewOff &&
            planDetail.subscription_status !=
              SubscriptionStatus.subscriptionExpired && (
              <p className={'mt-2'}>
                Looking to continue for another year?{' '}
                <a
                  href={'https://geniebook.com/contact'}
                  className={'text-primary-apricot underline font-bold'}
                >
                  Contact support
                </a>{' '}
                to renew.
              </p>
            )}
        </>
      );
    }

    if (
      planDetail.subscription_status ==
        SubscriptionStatus.subscriptionFailedChargeExpired ||
      planDetail.subscription_status ==
        SubscriptionStatus.subscriptionFailedChargeStillActive
    ) {
      return (
        <>
          Payment failed. You will automatically be charged the accumulated{' '}
          {getRenewalPrice(subscription, planDetail)} of{' '}
          <span className={'font-bold'}>failed charges</span> on{' '}
          <span className={'font-bold'}>{renewDate}</span> and each {timePeriod}{' '}
          thereafter.
          <p className={'mt-2'}>
            <a
              href={'https://geniebook.com/contact'}
              className={'text-primary-apricot underline font-bold'}
            >
              Contact support
            </a>{' '}
            for enquiries or to cancel your subscription.
          </p>
        </>
      );
    }

    return (
      <>
        You will automatically be charged{' '}
        {getRenewalPrice(subscription, planDetail)} on {renewDate} and each{' '}
        {timePeriod} thereafter.
        {isInAppPurchase(planDetail) && (
          <p className={'mt-2'}>
            Login to the Geniebook Mobile App or{' '}
            <a
              href={'https://geniebook.com/contact'}
              className={'text-primary-apricot underline font-bold'}
            >
              Contact support
            </a>{' '}
            to cancel your subscription.
          </p>
        )}
      </>
    );
  };

  return (
    <div
      className={
        'bg-primary-navyblue-shade lg:p-4 max-lg:p-3 flex rounded-2xl ' +
        (className ?? '')
      }
    >
      <div className={'flex-grow-0 flex-shrink-0 mr-3 pt-0.5'}>
        <TriangleExclamation
          className={'fill-current text-neutral-chalk w-4 h-4'}
        />
      </div>
      <div
        className={'flex-grow text-sm leading-[19px] text-neutral-chalk'}
        data-testid={'warning-content'}
      >
        <span className={'font-bold'}>NOTE:</span> {WarningContent()}
      </div>
    </div>
  );
};

export default WarningNote;
